import type { paths } from "@/api-schema";
import { Action, PathsV1VehicleUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import { useVehicleByUuidQuery, type VehicleByUuidResponse } from "@/common/api/vehicle/use-vehicle-by-uuid.query";
import { useVehicleDeleteMutation } from "@/common/api/vehicle/use-vehicle-delete.mutation";
import { getVehicleV1Identifier } from "@/common/helpers/get-vehicle-v1-identifier";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { useVehicleActions } from "@/common/hooks/vehicle/use-vehicle-actions";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { ConfirmationType } from "@/components/confirm-dialog/_types";
import { DeleteConfirmDialog } from "@/components/confirm-dialog/predefined/delete-confirm-dialog";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab } from "@/components/detail-page/tabs/system-id-tab";
import type { DetailAction, DetailTab } from "@/components/detail-page/types";
import { CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { CarFrontIcon, Fingerprint, MapPinPen, PencilIcon, ScaleIcon, TrashIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { VehicleDetailSkeleton } from "./_components/vehicle-detail-skeleton";
import { OverviewTab } from "./tabs/overview-tab/overview-tab";
import { RelocationOrdersTab } from "./tabs/relocation-orders-tab/relocation-orders-tab";
import { ValuationHistoryTab } from "./tabs/valuation-history-tab/valuation-history-tab";

const VEHICLE_DETAIL_EXTEND: PathsV1VehicleUuidGetParametersQueryExtend[] = [
  PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration,
  PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily,
  PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
  PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch,
  PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch_Company,
  PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch_Brands,
  PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch_Address,
  PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch,
  PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Company,
  PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Brands,
  PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Address,
  PathsV1VehicleUuidGetParametersQueryExtend.VehicleBlock,
  PathsV1VehicleUuidGetParametersQueryExtend.VehicleBlock_BlockedBy,
  PathsV1VehicleUuidGetParametersQueryExtend.RelocationOrders,
  PathsV1VehicleUuidGetParametersQueryExtend.ExchangeEngine,
  PathsV1VehicleUuidGetParametersQueryExtend.VehicleIdentifiers,
];

enum VehicleDetailTab {
  Overview = "overview",
  VehicleValuationHistory = "vehicle-valuation-history",
  RelocationOrders = "relocation-orders",
  SystemIdentifiers = "system-identifiers",
}

export function VehicleDetailView() {
  return (
    <Can I={Action.Read} a="Vehicle">
      <VehicleDetailQuery />
    </Can>
  );
}

function VehicleDetailQuery() {
  const { t } = useTranslation(["masterdata", "common"]);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid;

  const vehicleQueryParams = useMemo(
    () => ({
      parameters: {
        extend: VEHICLE_DETAIL_EXTEND,
      },
    }),
    [],
  );

  const vehicleQuery = useVehicleByUuidQuery(uuid, vehicleQueryParams);

  if (vehicleQuery.isPending) {
    return <VehicleDetailSkeleton />;
  }

  return (
    <UuidExistenceWrapper<
      paths["/v1/vehicle/{uuid}"]["get"]["parameters"]["query"],
      paths["/v1/vehicle/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
    >
      uuid={uuid}
      query={useVehicleByUuidQuery}
      queryResult={vehicleQuery}
      queryParams={vehicleQueryParams}
      notFoundProps={{
        title: t("masterdata:VehicleNotFound"),
        message: t("masterdata:VehicleDoesNotExist"),
        description: t("masterdata:CheckVehicleIdOrContactSupport"),
        backTo: "~/masterdata/vehicle",
        backToLabel: t("masterdata:BackToVehicleOverview"),
      }}
    >
      {vehicleQuery.data ? <VehicleDetailContent uuid={uuid} vehicle={vehicleQuery.data} /> : null}
    </UuidExistenceWrapper>
  );
}

function VehicleDetailContent({ uuid, vehicle }: { uuid: string; vehicle: VehicleByUuidResponse }) {
  const { t } = useTranslation(["masterdata", "common", "fab", "fit"]);
  const [_, navigate] = useLocation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { mutateAsync: deleteVehicleMutate } = useVehicleDeleteMutation();

  const { getActionsForVehicle, dialogs } = useVehicleActions();

  const handleEdit = useCallback(() => {
    navigate(`~/masterdata/vehicle/${uuid}/update`);
  }, [navigate, uuid]);

  const handleDelete = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    await toastMutationStatus({
      mutationFn: () => deleteVehicleMutate({ uuid }),
      successMessage: t("common:VehicleDeleted", { vin: vehicle.vin }),
      errorMessage: t("common:ErrorDeletingVehicle", { vin: vehicle.vin }),
      onSuccess: () => {
        setDeleteDialogOpen(false);
        navigate("~/masterdata/vehicle");
      },
    });
  }, [uuid, deleteVehicleMutate, vehicle.vin, t, navigate]);

  const brand = vehicle.modelGeneration?.modelFamily?.brand;
  const v1Identifier = getVehicleV1Identifier(vehicle);
  const modelName =
    vehicle.modelDescription || vehicle.modelGeneration?.name || vehicle.modelGeneration?.modelFamily?.name;
  const vehicleName = [brand?.name, modelName].filter(Boolean).join(" ") || vehicle.vin;

  const detailActions = useMemo<DetailAction[]>(
    () => [
      ...getActionsForVehicle(vehicle),
      {
        id: "edit-vehicle",
        label: t("common:Edit"),
        icon: PencilIcon,
        onClick: handleEdit,
        permission: { action: Action.Manage, subject: "Vehicle" },
      },
      {
        id: "delete-vehicle",
        label: t("common:Delete"),
        icon: TrashIcon,
        onClick: handleDelete,
        permission: { action: Action.Manage, subject: "Vehicle" },
        destructive: true,
      },
    ],
    [getActionsForVehicle, handleDelete, handleEdit, t, vehicle],
  );

  const tabs: DetailTab[] = [
    {
      id: VehicleDetailTab.Overview,
      label: t("common:Overview"),
      icon: CarFrontIcon,
      content: () => <OverviewTab uuid={uuid} vehicle={vehicle} />,
    },
    {
      id: VehicleDetailTab.VehicleValuationHistory,
      label: t("fab:VehicleValuationHistory"),
      icon: ScaleIcon,
      permission: { action: Action.Read, subject: "VehicleValuation" },
      content: () => <ValuationHistoryTab uuid={uuid} />,
    },
    {
      id: VehicleDetailTab.RelocationOrders,
      label: t("fit:RelocationOrders"),
      icon: MapPinPen,
      permission: { action: Action.Read, subject: "RelocationOrder" },
      content: () => <RelocationOrdersTab uuid={uuid} />,
    },
    {
      id: VehicleDetailTab.SystemIdentifiers,
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab
          uuid={vehicle.uuid}
          createdAt={vehicle.createdAt}
          updatedAt={vehicle.updatedAt}
          identifiers={[{ id: "v1-id", label: t("masterdata:V1ID"), value: v1Identifier }]}
        />
      ),
    },
  ];

  return (
    <>
      <DetailPageLayout
        variant={CenteredLayoutVariant.Wide}
        header={
          <DetailHeader
            icon={brand ? <BrandLogo variant={BrandLogoVariant.MEDIUM} brand={brand} /> : <CarFrontIcon />}
            title={vehicleName}
            subtitle={vehicleName === vehicle.vin ? undefined : vehicle.vin}
            actions={detailActions}
          />
        }
      >
        <DetailTabs sections={tabs} trackingName="vehicle" />
      </DetailPageLayout>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        label={t("common:Vehicle")}
        title={t("common:DeleteVehicle")}
        name={vehicle.vin}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        requireConfirmation={true}
        confirmationType={ConfirmationType.Name}
        showIrreversibleWarning={true}
      />

      {dialogs}
    </>
  );
}
